import mapKeys from 'lodash/mapKeys';
import pick from 'lodash/pick';
import snakeCase from 'lodash/snakeCase';

import jilConsumables from 'api/jil/jilConsumables';
import JilModelList from 'services/modelList/JilModelList';

import ConsumableUsage from './ConsumableUsage';

class ConsumableUsageList extends JilModelList {
  /**
   * @description Creates a new ConsumableUsageList instance.
   *
   * @param {Object} options - initialization object
   * @param {ConsumableUsage[]} options.items - entities which make up this list
   * @param {number} options.usageTotal - total usages available to be queried
   * @param {Consumable} parentConsumable - the Consumable to which this list belongs
   */
  constructor(options, parentConsumable) {
    super({
      itemClassRef: ConsumableUsage,
      itemCount: options.usageTotal || 0,
      resource: jilConsumables.getUsages,
      transformResponseData: transformItems,
    });

    this.items = transformItems(options.items);
    this.parentConsumable = parentConsumable;

    Object.assign(this, getQueryParams(this));
  }

  /**
   * @async
   * @description Method to fetch the updated array of ConsumableUsages
   */
  async refresh() {
    const params = {
      ...pick(this, [
        'contract_id',
        'delegation_type', // Remove with temp_sls_2_5
        'fulfillable_item_code',
        'fulfillment_id',
        'group_id',
        'include_depleted',
        'include_expired',
        'license_id',
      ]),
      orgId: this.organization_id,
    };

    await super.refresh(params);
  }
}

//////////////

/**
 * @description Method to get values from the parent Consumable object to be
 *     assigned to the model
 *
 * @param {ConsumableUsageList} model - the model with a parent Consumable
 *
 * @returns {Object} values to be assigned with keys converted to snake case
 */
function getQueryParams(model) {
  const parentParams = mapKeys(model?.parentConsumable?.getQueryParams(), (value, key) =>
    snakeCase(key)
  );

  return pick(parentParams, [
    'contract_id',
    'delegation_type', // Remove with temp_sls_2_5
    'fulfillable_item_code',
    'fulfillment_id',
    'group_id',
    'include_depleted',
    'include_expired',
    'license_id',
    'organization_id',
  ]);
}

/**
 * @description Method to transform the list of ConsumableUsage items
 *
 * @param {ConsumableUsage[]} items - the raw item data
 *
 * @returns {ConsumableUsage[]} the transformed ConsumableUsage items, or an
 *     empty array if the item data was missing
 */
function transformItems(items) {
  return items?.map((item) => ConsumableUsage.apiResponseTransformer(item)) || [];
}

export default ConsumableUsageList;
